import { useEffect, useRef } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import type { InspectionTemplate, TemplateSection } from 'inspection-types';

type Params = {
  template: InspectionTemplate | undefined;
  sortedSections: TemplateSection[];
  setExpandedBySectionId: Dispatch<
    SetStateAction<Record<string, boolean>>
  >;
  setActiveSectionId: Dispatch<SetStateAction<string | null>>;
};

export function useInspectionTemplateCustomizeExpandedState({
  template,
  sortedSections,
  setExpandedBySectionId,
  setActiveSectionId,
}: Params): void {
  const previousTemplateIdRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!template || sortedSections.length === 0) {
      return;
    }

    const shouldResetExpandedState =
      previousTemplateIdRef.current !== template.id;

    previousTemplateIdRef.current = template.id;

    setExpandedBySectionId(prev => {
      const next: Record<string, boolean> = {};

      for (const section of sortedSections) {
        const defaultExpanded = !section.config?.collapsedByDefault;

        next[section.id] = shouldResetExpandedState
          ? defaultExpanded
          : (prev[section.id] ?? defaultExpanded);
      }

      return next;
    });

    setActiveSectionId(prev => {
      if (prev && sortedSections.some(section => section.id === prev)) {
        return prev;
      }

      return sortedSections[0]?.id ?? null;
    });
  }, [template, sortedSections, setExpandedBySectionId, setActiveSectionId]);
}
